import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResendVerification from './pages/ResendVerification';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import AcceptInvitation from './pages/AcceptInvitation';
import Dashboard from './pages/Dashboard';
import Teams from './pages/Teams';
import Brands from './pages/Brands';
import Categories from './pages/Categories';
import MenuItems from './pages/MenuItems';
import Messages from './pages/Messages';
import BotSettings from './pages/BotSettings';
import CaptionSettings from './pages/CaptionSettings';
import AccountSettings from './pages/AccountSettings';
import APISettings from './pages/APISettings';
import Help from './pages/Help';
import SendMenu from './pages/SendMenu';
import Announcement from './pages/NewAnnouncement';
import ProductUpload from './pages/ProductUpload';
import ShopifySettings from './pages/ShopifySettings';
import FileRename from './pages/FileRename';
import DataMatch from './pages/DataMatch';
import VideoAudio from './pages/VideoAudio';
import VideoMatch from './pages/VideoMatch';
import QRPosterSettings from './pages/QRPosterSettings';
import QRPoster from './pages/QRPoster';
import Admin from './pages/Admin';
import Layout from './components/Layout';
import { Toaster } from './components/ui/sonner';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
  timeout: 600000, // 10 dakika timeout (büyük dosya yüklemeleri için)
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
});

axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axiosInstance.get('/auth/me')
        .then(res => {
          setUser(res.data);
          setIsAuthenticated(true);
          const teamId = localStorage.getItem('selectedTeamId');
          if (teamId) {
            setSelectedTeam(teamId);
          }
        })
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);
  
  // Handle post-login redirect from sessionStorage
  useEffect(() => {
    if (isAuthenticated && !loading) {
      const redirectUrl = sessionStorage.getItem('postLoginRedirect');
      if (redirectUrl) {
        sessionStorage.removeItem('postLoginRedirect');
        // Use a small delay to ensure state is fully updated
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 100);
      }
    }
  }, [isAuthenticated, loading]);

  const handleLogin = (token, userData, redirectUrl) => {
    localStorage.setItem('token', token);
    setUser(userData);
    setIsAuthenticated(true);
    
    // Store redirect URL if provided, will be used after state updates
    if (redirectUrl) {
      sessionStorage.setItem('postLoginRedirect', redirectUrl);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('selectedTeamId');
    setIsAuthenticated(false);
    setUser(null);
    setSelectedTeam(null);
  };

  const handleTeamChange = (teamId) => {
    setSelectedTeam(teamId);
    if (teamId) {
      localStorage.setItem('selectedTeamId', teamId);
    } else {
      localStorage.removeItem('selectedTeamId');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={
            !isAuthenticated ? <Login onLogin={handleLogin} /> : <Navigate to="/" />
          } />
          <Route path="/register" element={
            !isAuthenticated ? <Register onLogin={handleLogin} /> : <Navigate to="/" />
          } />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/resend-verification" element={<ResendVerification />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          
          {isAuthenticated ? (
            <Route path="/" element={
              <Layout 
                user={user} 
                onLogout={handleLogout} 
                selectedTeam={selectedTeam}
                onTeamChange={handleTeamChange}
              />
            }>
              <Route index element={<Dashboard selectedTeam={selectedTeam} />} />
              <Route path="teams" element={<Teams user={user} selectedTeam={selectedTeam} onTeamChange={handleTeamChange} />} />
              <Route path="brands" element={<Brands selectedTeam={selectedTeam} />} />
              <Route path="categories" element={<Categories selectedTeam={selectedTeam} />} />
              <Route path="menu-items" element={<MenuItems selectedTeam={selectedTeam} />} />
              <Route path="messages" element={<Messages selectedTeam={selectedTeam} />} />
              <Route path="product-upload" element={<ProductUpload selectedTeam={selectedTeam} />} />
              <Route path="shopify-settings" element={<ShopifySettings selectedTeam={selectedTeam} />} />
              <Route path="file-rename" element={<FileRename />} />
              <Route path="data-match" element={<DataMatch />} />
              <Route path="video-audio" element={<VideoAudio />} />
              <Route path="video-match" element={<VideoMatch />} />
              <Route path="qr-poster" element={<QRPoster selectedTeam={selectedTeam} />} />
              <Route path="qr-poster-settings" element={<QRPosterSettings selectedTeam={selectedTeam} />} />
              <Route path="bot-settings" element={<BotSettings selectedTeam={selectedTeam} />} />
              <Route path="caption-settings" element={<CaptionSettings selectedTeam={selectedTeam} />} />
              <Route path="api-settings" element={<APISettings selectedTeam={selectedTeam} />} />
              <Route path="account-settings" element={<AccountSettings user={user} onLogout={handleLogout} />} />
              <Route path="help" element={<Help />} />
              <Route path="send-menu" element={<SendMenu selectedTeam={selectedTeam} />} />
              <Route path="announcement" element={<Announcement selectedTeam={selectedTeam} />} />
              <Route path="admin" element={<Admin />} />
            </Route>
          ) : (
            <Route path="*" element={<Navigate to="/login" />} />
          )}
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
